// @ts-nocheck
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { GetAccountApiParameter } from "@insite/client-framework/Services/AccountService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadAccount";
import setUserFields from "@insite/client-framework/Store/Pages/UserSetup/Handlers/SetUserFields";
import { getCurrentEditingUser } from "@insite/client-framework/Store/Pages/UserSetup/UserSetupSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { formGridProps } from "@store/Themes/common/props/form";
/* Starter Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    editingUser: getCurrentEditingUser(state),
});

const mapDispatchToProps = {
    setUserFields,
    loadAccount,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface UserSetupSettingsStyles {
    title?: TypographyPresentationProps;
    fieldset?: InjectableCss;
    wrapper?: InjectableCss;
    userRoleSelect?: SelectPresentationProps;
    approverSelect?: SelectPresentationProps;
    approverDynamicDropdown?: DynamicDropdownPresentationProps;
    // Starter Custom Code
    container?: GridContainerProps;
    gridItem?: GridItemProps;
}

export const userSetupSettingsStyles: UserSetupSettingsStyles = {
    title: formGridProps.title,
    container: formGridProps.gridContainer,
    gridItem: { width: [12, 6, 6, 6, 6] },
};

const styles = userSetupSettingsStyles;
const StyledFieldSet = getStyledWrapper("div"); // Starter Custom Code

const UserSetupSettings = ({ editingUser, setUserFields, loadAccount }: Props) => {
    // Starter Moved - if (!editingUser) { - down

    const [options, setOptions] = useState<OptionObject[]>([]);

    useEffect(() => {
        /* Starter Custom Code - Start */
        if (!editingUser) {
            return;
        }
        /* Starter Custom Code - End */

        if (options.length < 1) {
            setApproverOptions("");
        }

        loadAccount({ accountId: editingUser.id, numberOfApprovers: 20 });
    }, [editingUser]);

    /* Starter Custom Code - Start */
    if (!editingUser) {
        return null;
    }
    /* Starter Custom Code - End */

    const userRoleChangeHandler: SelectProps["onChange"] = event => {
        setUserFields({ role: event.target.value });
        setUserFields({ approver: "" });
    };

    const approverChangeHandler = (value?: string) => {
        const approverValue = value || "";
        setUserFields({ approver: approverValue });
    };

    const onInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const filterValue = event.currentTarget.value;
        debouncedSearchApprovers(filterValue);
    };

    const debouncedSearchApprovers = debounce((filterValue: string) => {
        setApproverOptions(filterValue);
    }, 300);

    const setApproverOptions = (filterValue: string) => {
        const currentAccountId = editingUser.id;
        if (!currentAccountId) {
            return;
        }

        let approverOptions: OptionObject[] = [{ optionText: translate("Select an Approver"), optionValue: "" }];
        const parameter = {
            accountId: currentAccountId,
            numberOfApprovers: 20,
        } as GetAccountApiParameter;

        if (filterValue !== "") {
            parameter.approversUsernameFilter = filterValue;
        }

        loadAccount({
            ...parameter,
            onComplete: result => {
                if (!result?.apiResult?.availableApprovers) {
                    return;
                }

                approverOptions = approverOptions.concat(
                    result.apiResult.availableApprovers.map(o => ({ optionText: o, optionValue: o })),
                );

                setOptions(approverOptions);
            },
        });
    };

    return (
        <>
            <Typography {...styles.title} data-test-selector="userSetupSetting_title" /* Starter Custom Code */>
                {translate("Settings")}
            </Typography>
            <StyledFieldSet {...styles.fieldset}>
                <GridContainer {...styles.container} /* Starter Custom Code */>
                    <GridItem {...styles.gridItem} /* Starter Custom Code */>
                        <Select
                            {...styles.userRoleSelect}
                            label={translate("User Role")}
                            value={editingUser.role}
                            onChange={userRoleChangeHandler}
                            data-test-selector="userSetupSetting_userRole"
                        >
                            <option value="">{translate("Select User Role")}</option>
                            {editingUser.availableRoles?.map(role => (
                                <option key={role} value={role}>
                                    {(editingUser.availableRolesTranslated &&
                                        editingUser.availableRolesTranslated[role.toLowerCase()]) ||
                                        role}
                                </option>
                            ))}
                        </Select>
                    </GridItem>
                    <GridItem {...styles.gridItem} /* Starter Custom Code */>
                        <DynamicDropdown
                            {...styles.approverDynamicDropdown}
                            label={translate("Assign Approver")}
                            onInputChange={onInputChanged}
                            onSelectionChange={approverChangeHandler}
                            selected={editingUser.approver}
                            options={options}
                            data-test-selector="userSetupSetting_assignApprover"
                            placeholder={translate("Select an Approver")}
                        />
                    </GridItem>
                </GridContainer>
            </StyledFieldSet>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UserSetupSettings),
    definition: {
        group: "User Setup",
        displayName: "Settings",
        allowedContexts: ["UserSetupPage"],
    },
};

export default widgetModule;
